import React from "react";
import { motion, AnimatePresence } from "framer-motion";

import { Close } from "@mui/icons-material";

import { Box, Button, IconButton, Modal, Typography, useTheme } from "@mui/material";

import Loader from "../Loading";

import { loaderStyle } from "../../GeneralStyles";

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 300,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.15 },
  },
};

const styleButton = {
  padding: "0.6rem 1.5rem",
  fontSize: "0.875rem",
  textTransform: "none",
  borderRadius: "8px",
  fontWeight: 600,
};

export default function ModalDialog(props) {
  const {
    open,
    handleClose,
    title,
    content,
    closeLabel,
    onSave,
    saveLabel,
    // Destructive-action flag. When true, the primary button switches
    // from brand-orange to red so the user reads it as "danger, this
    // will delete" rather than "primary action = save my changes".
    dangerous,
    specificDetails,
    sx,
  } = props;

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Theme-responsive colors
  const modalColors = {
    contentBg: isDarkMode ? "#1e293b" : "#ffffff",
    footerBg: isDarkMode ? "#0f172a" : "#f8fafc",
    border: isDarkMode ? "#334155" : "#e2e8f0",
    text: isDarkMode ? "#e2e8f0" : "#334155",
    textMuted: isDarkMode ? "#94a3b8" : "#64748b",
    buttonText: isDarkMode ? "#e2e8f0" : "#475569",
    buttonBorder: isDarkMode ? "#475569" : "#cbd5e1",
    buttonHoverBg: isDarkMode ? "#334155" : "#f1f5f9",
  };

  const showSave = saveLabel || false;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="child-modal-title"
      aria-describedby="child-modal-description"
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(15, 23, 42, 0.7)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          pointerEvents: "none",
        }}
      >
        <AnimatePresence>
          {open && (
            <Box
              component={motion.div}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              sx={{
                width: { xs: "90vw", md: "50em", lg: "70em" },
                maxWidth: "95vw",
                bgcolor: modalColors.contentBg,
                borderRadius: "12px",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                overflow: "hidden",
                maxHeight: "90dvh",
                display: "flex",
                flexDirection: "column",
                pointerEvents: "auto",
                transition: "background-color 0.3s ease",
                ...sx,
                ...(specificDetails?.style?.dialog ?? {}),
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  px: 3,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #334155",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#f1f5f9",
                    fontSize: "1.1rem",
                  }}
                >
                  {title}
                </Typography>
                {handleClose && (
                  <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                      color: "#94a3b8",
                      "&:hover": {
                        background: "rgba(255,255,255,0.1)",
                        color: "#f1f5f9",
                      },
                    }}
                  >
                    <Close sx={{ fontSize: "1.25rem" }} />
                  </IconButton>
                )}
              </Box>

              {/* Content */}
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  p: 3,
                  ...(specificDetails?.style?.contentModal || {}),
                }}
              >
                {content}
              </Box>

              {/* Footer */}
              {!specificDetails?.shouldHideFooter && (handleClose || showSave) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    position: "sticky",
                    bottom: 0,
                    background: modalColors.footerBg,
                    borderTop: `1px solid ${modalColors.border}`,
                    px: 3,
                    py: 2,
                    zIndex: 10,
                    flexShrink: 0,
                    transition: "background-color 0.3s ease, border-color 0.3s ease",
                  }}
                >
                  {handleClose && (
                    <Button
                      sx={{
                        ...styleButton,
                        color: modalColors.buttonText,
                        borderColor: modalColors.buttonBorder,
                        "&:hover": {
                          borderColor: isDarkMode ? "#64748b" : "#94a3b8",
                          background: modalColors.buttonHoverBg,
                        },
                      }}
                      variant="outlined"
                      onClick={handleClose}
                    >
                      {closeLabel}
                    </Button>
                  )}
                  {showSave && (
                    <Button
                      sx={{
                        ...styleButton,
                        background: dangerous
                          ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                          : "linear-gradient(135deg, #F97316 0%, #EA580C 100%)",
                        "&:hover": {
                          background: dangerous
                            ? "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
                            : "linear-gradient(135deg, #EA580C 0%, #C2410C 100%)",
                        },
                      }}
                      variant="contained"
                      onClick={onSave}
                      disabled={specificDetails?.isSubmitting || specificDetails?.saveDisabled}
                    >
                      {!specificDetails?.isSubmitting ? (
                        saveLabel
                      ) : (
                        <Loader
                          specificDetails={{
                            size: 20,
                            style: loaderStyle,
                          }}
                        />
                      )}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}
        </AnimatePresence>
      </Box>
    </Modal>
  );
}
